import React from 'react';
import PropTypes from 'prop-types';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
}                                       from '@material-ui/core';

import { makeStyles, useTheme }         from '@material-ui/core/styles';

import { PieChart }                     from 'react-minimal-pie-chart';

import { 
  fmtBigInteger,
  fmtBytes,
  fmtBytesConcise,
  fmtCurrency,
  fmtCurrencyConcise,
}                                       from '../../utils/formatters';

const useStyles = makeStyles((theme) => ({
  root: {
    // padding: 10,  // caused horizontal scrollbars on card 
  },
  table: {
    minWidth: 250,
    },
  cell: {
    padding: 0,
  },
}));

export default function InventoryResults(props) {
    
  const { value, variant } = props;
  
  const classes = useStyles();

  const theme = useTheme();
  const inventory = value.Metrics.Inventory;
  const storage = inventory?.Latest?.Data?.Storage;

  if(!storage) {
    return(null);
  }
  
  const code_map = {
    'S':      { 'order': 1, 'label': "Standard", 'color': theme.palette.secondary.dark },
    'SIA':    { 'order': 2, 'label': "StandardIA", 'color': theme.palette.error.main },
    'OIA':    { 'order': 3, 'label': "OneZoneIA", 'color': theme.palette.error.light },
    'GIR':    { 'order': 4, 'label': "GlacierInstantRetrieval", 'color': theme.palette.success.main },
    'DA':     { 'order': 5, 'label': "DeepArchive", 'color': theme.palette.success.dark },
    'IT/F':   { 'order': 6, 'label': "IT/FrequentAccess", 'color': theme.palette.warning.dark },
    'IT/I':   { 'order': 7, 'label': "IT/InfrequentAccess", 'color': theme.palette.info.light },
    'IT/AIA': { 'order': 8, 'label': "IT/ArchiveInstantAccess", 'color': theme.palette.info.dark },
    'RR':     { 'order': 9, 'label': "ReducedRedundancy", 'color': 'grey' },
  };
  
  const data_table=[];
  const headings=[ 'Storage Class/Tier', 'Objects', 'Size', 'Est. $/mo' ];
  const totals=[0, null, 'Overall', 0, 0, 0.0 ];
  
  storage.forEach(function (r, i) {
    const row=[...r];
    if(code_map[row[0]]) {
      const v=code_map[row[0]];
      row[0]=v.label;
      row.unshift(v.color);
      row.unshift(v.order);
    } else {
      row.unshift('grey');
      row.unshift(code_map.length+1);
    }
    row.forEach(function (col, j) {
      if(j>2) {
        totals[j]+=col;
      } 
    });
    data_table.push(row);
  });  
  data_table.unshift(totals);
  
  var sorted_data_table = data_table.sort(function(a, b) {
    return a[0] - b[0];
  });  

  if(variant==='table') {
    return (
      <TableContainer className={classes.root}>
        <Table className={classes.table} size="small">
          <TableHead style={{ visibility: 'visible'}} >
            <TableRow>
              {
                headings.map((col, ci) => {
                  return(
                    <TableCell key={ci} className={classes.cell} align={ci?"right":"left"}>
                      <Typography variant='caption'>{col}</Typography>
                    </TableCell>
                  );
                })
              }
            </TableRow>
          </TableHead>
          <TableBody>
            {
              sorted_data_table.map((row, ri) => {
                const color=row[1];
                const row_data=row.slice(2);
                return(
                  <TableRow key={ri}>
                    {
                      row_data.map((col, ci) => {
                        let val=col;
                        if(ci===1) {
                            val=fmtBigInteger(col);
                        } else if(ci===2) {
                            val=fmtBytes(col);
                        } else if(ci===3) {
                            val=fmtCurrency(col);
                        }
                        return(
                          <TableCell key={ci} className={classes.cell} align={ci?"right":"left"}>
                            {color&&ci===0?
                              <i><Typography variant={ci?"inherit":"caption"}><span style={{backgroundColor: color}}>&nbsp;</span>&nbsp;{val}</Typography></i>
                              :
                              <Typography variant={ci?"inherit":"caption"}>{val}</Typography>
                            }
                          </TableCell>
                        );
                      })
                    }
                  </TableRow>
                );
              })
            }
          
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else {
    const empty_pie=[{ title: "Empty", value: 1, color: '#eeeeee' }];
    const objs_pie=sorted_data_table.slice(1).map((row, ri) => ({ title: row[2], value: row[3], color: row[1] })).filter((item) => item.value>0);
    let size_pie=sorted_data_table.slice(1).map((row, ri) => ({ title: row[2], value: row[4], color: row[1] })).filter((item) => item.value>0);
    let cost_pie=sorted_data_table.slice(1).map((row, ri) => ({ title: row[2], value: row[5], color: row[1] })).filter((item) => item.value>0);
    if(!size_pie.length) {
      size_pie=objs_pie.length?objs_pie:empty_pie;
    }
    if(!cost_pie.length) {
      cost_pie=objs_pie.length?objs_pie:empty_pie;
    }
    return(
      <div style={{ display: 'flex', paddingRight: '8px' }}>
        <div style={{ display: 'flex' }}>
          <Typography variant='caption'>Size</Typography>
          <PieChart style={{ height: '75px', width: 'auto' }}
            animate
            lineWidth={35}
            paddingAngle={5}
            lengthAngle={360}
            startAngle={270}
            label={() => fmtBytesConcise(inventory.Latest.Data.TotalObjectBytes)}
            labelStyle={{
              fontSize: '20px',
              fontFamily: 'sans-serif',
            }}
            labelPosition={0}
            data={size_pie}
          />
        </div>
        <div style={{ display: 'flex' }}>
          <Typography variant='caption'>Cost</Typography>
          <PieChart style={{ height: '75px', width: 'auto' }}
            animate
            lineWidth={35}
            paddingAngle={5}
            lengthAngle={360}
            startAngle={270}
            label={() => fmtCurrencyConcise(inventory.Latest.Data.Cost.Estimated.Monthly.Total)}
            labelStyle={{
              fontSize: '20px',
              fontFamily: 'sans-serif',
            }}
            labelPosition={0}
            data={cost_pie}
          />
        </div>
      </div>
    );
  }
}

InventoryResults.propTypes = {
  value: PropTypes.object.isRequired,
  variant: PropTypes.string.isRequired,
};

InventoryResults.defaultProps = {
  variant: 'table',
};
